import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  input,
  output,
  untracked,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslatePipe } from '@ngx-translate/core';
import { Permission } from '@phoenix/common';
import { InputText } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { lastValueFrom } from 'rxjs';
import { I18nService } from '../../../../@core/services/i18n/i18n.service';
import { MenuService } from '../../../../@core/services/menu.service';
import { Menu, Operator, Role } from '../../../../generated/generated-types';
import { LabeledDataComponent } from '../../../../shared/components/labeled-data/labeled-data.component';
import { PhxCheckboxComponent } from '../../../../shared/components/phx-input-checkbox/phx-input-checkbox.component';
import { PhxSelect } from '../../../../shared/components/phx-select/phx-select.component';
import { MenuDropDownComponent } from '../../../../shared/controls/menu-drop-down/menu-drop-down.component';
import { RoleDropDownComponent } from '../../../../shared/controls/role-drop-down/role-drop-down.component';
import { PermissionsService } from '../../../../shared/services/permissions.service';
import { RoleService } from '../../../../shared/services/role.service';
import { CustomEntitySchemaService } from '../../services/custom-entity-schema.service';
import {
  EditableNavigationItem,
  NavigationItemBadge,
  NavigationItemType,
  buildCustomEntityListLink,
  isNavigableLeafType,
} from './menu-navigation-item.types';

interface PermissionSelectOption {
  readonly label: string;
  readonly value: string;
}

interface TypeSelectOption {
  readonly label: string;
  readonly value: NavigationItemType;
}

interface CustomEntitySelectOption {
  readonly label: string;
  readonly value: string;
}

@Component({
  selector: 'app-menu-item-inspector',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [RoleService, PermissionsService, MenuService, CustomEntitySchemaService],
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
    InputText,
    SelectModule,
    PhxCheckboxComponent,
    PhxSelect,
    RoleDropDownComponent,
    MenuDropDownComponent,
    LabeledDataComponent,
  ],
  templateUrl: './menu-item-inspector.component.html',
})
export class MenuItemInspectorComponent {
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly i18n = inject(I18nService);
  private readonly roleService = inject(RoleService);
  private readonly menuService = inject(MenuService);
  private readonly permissionsService = inject(PermissionsService);
  private readonly customEntitySchemaService = inject(CustomEntitySchemaService);

  readonly item = input<EditableNavigationItem | null>(null);
  /** When true, item is a child of a dropdown — only navigable leaf types are allowed. */
  readonly isNestedUnderDropdown = input(false);
  /** UUID of the menu currently being edited — excluded from menu references. */
  readonly excludeMenuId = input<string | null>(null);
  readonly itemChange = output<EditableNavigationItem>();
  /** Emits the resolved Menu when the inspector loads/selects a referenced menu (for tree labels). */
  readonly referencedMenuResolved = output<Menu | null>();

  permissionOptions: string[] = [];
  customEntityOptions: CustomEntitySelectOption[] = [];
  selectedRoles: Role[] = [];
  selectedReferencedMenu: Menu | null = null;

  private readonly allTypeOptions: TypeSelectOption[];

  private lastSyncedRoleIdsKey = '';
  private lastSyncedMenuId = '';
  /** Blocks role/permission control echoes from emitting while applying selection. */
  private suppressItemChange = false;

  constructor() {
    this.allTypeOptions = [
      { value: 'simple', label: this.i18n.translate(_('menu-items-editor.type-simple')) },
      { value: 'dropdown', label: this.i18n.translate(_('menu-items-editor.type-dropdown')) },
      { value: 'header', label: this.i18n.translate(_('menu-items-editor.type-header')) },
      { value: 'menu', label: this.i18n.translate(_('menu-items-editor.type-menu')) },
      {
        value: 'customEntity',
        label: this.i18n.translate(_('menu-items-editor.type-custom-entity')),
      },
    ];
    void this.loadPermissionOptions();
    void this.loadCustomEntityOptions();

    effect(() => {
      const current = this.item();
      const roleIdsKey = (current?.requiredRoleIds ?? []).join(',');
      untracked(() => {
        if (!current) {
          this.lastSyncedRoleIdsKey = '';
          this.setSelectedRolesWithoutEmit([]);
          return;
        }
        if (roleIdsKey === this.lastSyncedRoleIdsKey) {
          return;
        }
        this.lastSyncedRoleIdsKey = roleIdsKey;
        void this.syncRolesFromItem(current);
      });
    });

    effect(() => {
      const current = this.item();
      const menuId = current?.type === 'menu' ? (current.menuId ?? '') : '';
      untracked(() => {
        if (!current || current.type !== 'menu') {
          this.lastSyncedMenuId = '';
          this.setSelectedReferencedMenuWithoutEmit(null);
          return;
        }
        if (menuId === this.lastSyncedMenuId) {
          return;
        }
        this.lastSyncedMenuId = menuId;
        void this.syncReferencedMenuFromItem(current);
      });
    });
  }

  get typeOptions(): TypeSelectOption[] {
    if (this.isNestedUnderDropdown()) {
      return this.allTypeOptions.filter(option => isNavigableLeafType(option.value));
    }
    return this.allTypeOptions;
  }

  get badgeText(): string {
    return this.item()?.badge?.text ?? '';
  }

  get badgeClass(): string {
    return this.item()?.badge?.class ?? '';
  }

  get isHeaderType(): boolean {
    return this.item()?.type === 'header';
  }

  get isDropdownType(): boolean {
    return this.item()?.type === 'dropdown';
  }

  get isSimpleType(): boolean {
    return this.item()?.type === 'simple';
  }

  get isCustomEntityType(): boolean {
    return this.item()?.type === 'customEntity';
  }

  get isMenuType(): boolean {
    return this.item()?.type === 'menu';
  }

  get isNavigableLeaf(): boolean {
    return isNavigableLeafType(this.item()?.type);
  }

  /** Icons: dropdowns, and root-level navigable leaves (not nested under a dropdown). */
  get supportsIcon(): boolean {
    if (this.isDropdownType) {
      return true;
    }
    return this.isNavigableLeaf && !this.isNestedUnderDropdown();
  }

  onTitleChange(value: string): void {
    this.patchItem({ title: value });
  }

  onTypeChange(value: NavigationItemType | string | null): void {
    if (!value) {
      return;
    }
    if (this.isNestedUnderDropdown() && !isNavigableLeafType(value)) {
      return;
    }
    const partial: Partial<EditableNavigationItem> = { type: value };
    const keepsIcon =
      value === 'dropdown' || (isNavigableLeafType(value) && !this.isNestedUnderDropdown());
    if (!keepsIcon) {
      partial.icon = undefined;
    }
    if (value !== 'dropdown') {
      partial.children = [];
    }
    if (!isNavigableLeafType(value)) {
      partial.allowFavorite = false;
    }
    if (value !== 'simple') {
      partial.link = undefined;
    }
    if (value !== 'customEntity') {
      partial.customEntityIdentifier = undefined;
    }
    if (value === 'menu') {
      partial.title = undefined;
      partial.badge = undefined;
    }
    if (value === 'header') {
      partial.badge = undefined;
    }
    if (value !== 'menu') {
      partial.menuId = undefined;
    }
    this.patchItem(partial);
  }

  onLinkChange(value: string): void {
    this.patchItem({ link: value });
  }

  onCustomEntityChange(value: string | CustomEntitySelectOption | null | undefined): void {
    let identifier: string | undefined;
    if (typeof value === 'string') {
      identifier = value.trim() || undefined;
    } else if (value && typeof value === 'object' && 'value' in value) {
      identifier = value.value?.trim() || undefined;
    }
    this.patchItem({
      customEntityIdentifier: identifier,
      link: identifier ? buildCustomEntityListLink(identifier) : undefined,
    });
  }

  onIconChange(value: string): void {
    this.patchItem({ icon: value });
  }

  onKeyChange(value: string): void {
    this.patchItem({ key: value });
  }

  onAllowFavoriteChange(value: boolean): void {
    this.patchItem({ allowFavorite: !!value });
  }

  onVisibleInSidebarChange(value: boolean): void {
    this.patchItem({ hidden: !value });
  }

  onReferencedMenuChange(menu: Menu | Menu[] | null): void {
    if (this.suppressItemChange) {
      return;
    }
    const selected = Array.isArray(menu) ? (menu[0] ?? null) : menu;
    const excludeId = this.excludeMenuId();
    if (selected?.id && excludeId && selected.id === excludeId) {
      this.setSelectedReferencedMenuWithoutEmit(null);
      this.referencedMenuResolved.emit(null);
      this.patchItem({ menuId: undefined, title: undefined });
      return;
    }
    this.selectedReferencedMenu = selected;
    this.lastSyncedMenuId = selected?.id ?? '';
    this.referencedMenuResolved.emit(selected);
    this.patchItem({ menuId: selected?.id ?? undefined, title: undefined });
  }

  onPermissionsChange(value: string[] | string | null | undefined): void {
    if (Array.isArray(value)) {
      this.patchItem({ requiresPermission: value.filter(Boolean) });
    } else if (value) {
      this.patchItem({ requiresPermission: [value] });
    } else {
      this.patchItem({ requiresPermission: [] });
    }
  }

  onSelectedRolesChange(roles: Role | Role[] | null): void {
    if (this.suppressItemChange) {
      return;
    }
    const list = Array.isArray(roles) ? roles : roles ? [roles] : [];
    this.selectedRoles = list;
    this.patchItem({
      requiredRoleIds: list.map(r => r.id).filter((id): id is string => !!id),
    });
  }

  onBadgeTextChange(value: string): void {
    this.patchBadge({ text: value });
  }

  onBadgeClassChange(value: string): void {
    this.patchBadge({ class: value });
  }

  private patchItem(partial: Partial<EditableNavigationItem>): void {
    if (this.suppressItemChange) {
      return;
    }
    const current = this.item();
    if (!current || !this.hasMeaningfulChange(current, partial)) {
      return;
    }
    this.itemChange.emit({ ...current, ...partial });
  }

  private hasMeaningfulChange(
    current: EditableNavigationItem,
    partial: Partial<EditableNavigationItem>,
  ): boolean {
    for (const key of Object.keys(partial) as (keyof EditableNavigationItem)[]) {
      const next = partial[key];
      const prev = current[key];
      if (key === 'requiresPermission' || key === 'requiredRoleIds') {
        const prevIds = (prev as string[] | undefined) ?? [];
        const nextIds = (next as string[] | undefined) ?? [];
        if (
          prevIds.length !== nextIds.length ||
          prevIds.some((id, index) => id !== nextIds[index])
        ) {
          return true;
        }
        continue;
      }
      if (key === 'badge') {
        if (JSON.stringify(prev ?? null) !== JSON.stringify(next ?? null)) {
          return true;
        }
        continue;
      }
      if (key === 'allowFavorite' || key === 'hidden') {
        if (!!prev !== !!next) {
          return true;
        }
        continue;
      }
      if (key === 'children') {
        const prevChildren = (prev as EditableNavigationItem[] | undefined) ?? [];
        const nextChildren = (next as EditableNavigationItem[] | undefined) ?? [];
        if (prevChildren.length !== nextChildren.length) {
          return true;
        }
        continue;
      }
      if ((prev ?? '') !== (next ?? '')) {
        return true;
      }
    }
    return false;
  }

  private patchBadge(partial: NavigationItemBadge): void {
    const current = this.item();
    if (!current) {
      return;
    }
    const nextBadge: NavigationItemBadge = {
      ...(current.badge ?? {}),
      ...partial,
    };
    const text = nextBadge.text?.trim() ?? '';
    const cssClass = nextBadge.class?.trim() ?? '';
    this.patchItem({
      badge: text || cssClass ? { text: nextBadge.text, class: nextBadge.class } : undefined,
    });
  }

  private async loadPermissionOptions(): Promise<void> {
    try {
      const res = await lastValueFrom(this.permissionsService.getAllPermissions().single$);
      const permissions = ((res as { getAllPermissions?: string[] })?.getAllPermissions ?? []) as string[];
      this.permissionOptions = permissions;//this.toPermissionSelectOptions(permissions);
    } catch {
      this.permissionOptions = Object.values(Permission);//this.toPermissionSelectOptions(Object.values(Permission));
    }
    this.cdRef.markForCheck();
  }

  private async loadCustomEntityOptions(): Promise<void> {
    try {
      const res = await lastValueFrom(this.customEntitySchemaService.getItems({}).single$);
      const items = res.getCustomEntitySchemas?.items ?? [];
      this.customEntityOptions = items
        .filter((entity): entity is typeof entity & { identifier: string } => !!entity.identifier)
        .map(entity => ({
          label: entity.displayName?.trim() || entity.identifier,
          value: entity.identifier,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    } catch {
      this.customEntityOptions = [];
    }
    this.cdRef.markForCheck();
  }

  private toPermissionSelectOptions(permissions: string[]): PermissionSelectOption[] {
    return [...new Set(permissions.filter(Boolean))]
      .sort((a, b) => a.localeCompare(b))
      .map(p => ({ label: p, value: p }));
  }

  private setSelectedRolesWithoutEmit(roles: Role[]): void {
    this.suppressItemChange = true;
    this.selectedRoles = roles;
    this.cdRef.markForCheck();
    queueMicrotask(() => {
      this.suppressItemChange = false;
    });
  }

  private setSelectedReferencedMenuWithoutEmit(menu: Menu | null): void {
    this.suppressItemChange = true;
    this.selectedReferencedMenu = menu;
    this.cdRef.markForCheck();
    queueMicrotask(() => {
      this.suppressItemChange = false;
    });
  }

  private async syncRolesFromItem(current: EditableNavigationItem | null): Promise<void> {
    const roleIds = current?.requiredRoleIds ?? [];
    if (!roleIds.length) {
      this.setSelectedRolesWithoutEmit([]);
      return;
    }

    this.suppressItemChange = true;
    try {
      const res = await lastValueFrom(
        this.roleService.getItems({
          searchFilter: {
            field: 'id',
            operator: Operator.in,
            value: JSON.stringify(roleIds),
            type: 'array',
          },
          take: roleIds.length,
        }).single$,
      );
      this.selectedRoles = (res.getRoles?.items ?? []) as Role[];
    } catch {
      this.selectedRoles = [];
    }
    this.cdRef.markForCheck();
    queueMicrotask(() => {
      this.suppressItemChange = false;
    });
  }

  private async syncReferencedMenuFromItem(current: EditableNavigationItem | null): Promise<void> {
    const menuId = current?.menuId?.trim();
    if (!menuId) {
      this.setSelectedReferencedMenuWithoutEmit(null);
      return;
    }

    this.suppressItemChange = true;
    try {
      const res = await lastValueFrom(this.menuService.getItem(menuId).single$);
      this.selectedReferencedMenu = (res.getMenu as Menu | null) ?? null;
      this.referencedMenuResolved.emit(this.selectedReferencedMenu);
    } catch {
      this.selectedReferencedMenu = null;
    }
    this.cdRef.markForCheck();
    queueMicrotask(() => {
      this.suppressItemChange = false;
    });
  }
}
